import { API_BASE_URL } from "src/config/api";
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { noCase } from 'change-case';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Popover,
  Typography,
  IconButton,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  CircularProgress,
} from '@mui/material';

import { fToNow } from '../../../utils/formatTime';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();

  const totalUnRead = alerts.filter((item) => item.isUnRead === true).length;

  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);

      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');

      if (token && userId) {
        try {
          const config = {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };


          const response = await axios.get(`${API_BASE_URL}/getAlertByUserId?userId=${userId}`, config);

          console.log("response",response.data.alerts)
          // Assume alerts come as an array of objects
          setAlerts(
            response.data.alerts.map((item) => ({
              id: item._id,
              title: item.alertname || 'Alert',
              description: item.annotations?.description || 'No description',
              type: item.labels?.type || 'alert',
              avatar: null,
              isUnRead: item.status !== 'resolved',
              createdAt: new Date(item.receivedAt),
            }))
          );
          
          setError(null);
        } catch (err) {
          setError('Failed to load alerts');
        } finally {
          setLoading(false);
        }
      };
    }

    fetchAlerts();
  }, []);

  const handleOpen = (event) => setOpen(event.currentTarget);
  const handleClose = () => setOpen(null);

  const handleMarkAllAsRead = () => {
    setAlerts(alerts.map((alert) => ({ ...alert, isUnRead: false })));
  };

  const handleClick = () => {
    navigate(`/dashboard/alert`);
    handleClose();
  };

  return (
    <>
      <IconButton color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" width={25} height={25}/>
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1.5, ml: 0.75, width: 360 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Alerts</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You have {totalUnRead} unread alerts
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
              <List
                disablePadding
                subheader={
                  <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                    New
                  </ListSubheader>
                }
              >
                {alerts.slice(0, 2).map((alert) => (
                  <AlertItem key={alert.id} alert={alert} />
                ))}
              </List>

              <List
                disablePadding
                subheader={
                  <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                    Before that
                  </ListSubheader>
                }
              >
                {alerts.slice(2, 5).map((alert) => (
                  <AlertItem key={alert.id} alert={alert} />
                ))}
              </List>
            </Scrollbar>

            <Divider sx={{ borderStyle: 'dashed' }} />

            <Box sx={{ p: 1 }}>
              <Button fullWidth disableRipple onClick={handleClick}>
                View All
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}

// ----------------------------------------------------------------------

AlertItem.propTypes = {
  alert: PropTypes.shape({
    createdAt: PropTypes.instanceOf(Date),
    id: PropTypes.string,
    isUnRead: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    avatar: PropTypes.any,
  }),
};

function AlertItem({ alert }) {
  const { avatar, title } = renderContent(alert);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(alert.isUnRead && { bgcolor: 'action.selected' }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{ mt: 0.5, display: 'flex', alignItems: 'center', color: 'text.disabled' }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fToNow(alert.createdAt)}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(alert) {
  const title = (
    <Typography variant="subtitle2">
      {alert.title}
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        &nbsp; {noCase(alert.description)}
      </Typography>
    </Typography>
  );

  return {
    avatar: <img alt={alert.title} src={`/assets/icons/ic_notification_chat.svg`} />,
    title,
  };
}
